import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* doctor-data-store actions */
import * as DoctorDataStoreActions from 'src/app/features/data-stores/doctor-data-store/state/doctor-data-store.actions';

/* api services */
import { DoctorControllerService } from 'src/app/features/shared/sdk/services';

@Injectable()
export class DoctorDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private doctorApiService: DoctorControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  getDoctors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorDataStoreActions.GetDoctors),
      mergeMap((action) => {
        return this.doctorApiService.find().pipe(
          mergeMap((doctors) => {
            const payload: any = {
              doctors: doctors,
            };
            return [DoctorDataStoreActions.GetDoctorsSuccessful({ payload })];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [DoctorDataStoreActions.GetDoctorsFailure()];
          }),
        );
      }),
    ),
  );

  getDoctorById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorDataStoreActions.GetDoctorById),
      mergeMap((action) => {
        return this.doctorApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((doctor) => {
              const payload: any = {
                doctor: doctor,
              };
              return [
                DoctorDataStoreActions.GetDoctorByIdSuccessful({ payload }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [DoctorDataStoreActions.GetDoctorByIdFailure()];
            }),
          );
      }),
    ),
  );

  createDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorDataStoreActions.CreateDoctor),
      mergeMap((action) => {
        const params = {
          body: action.payload.doctor,
        };
        return this.doctorApiService.create(<any>params).pipe(
          mergeMap((doctor) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              doctor: doctor,
            };
            return [DoctorDataStoreActions.CreateDoctorSuccessful({ payload })];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [DoctorDataStoreActions.CreateDoctorFailure()];
          }),
        );
      }),
    ),
  );

  updateDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorDataStoreActions.UpdateDoctor),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.doctorApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been updated successfully.',
            );
            return [DoctorDataStoreActions.GetDoctors()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [DoctorDataStoreActions.UpdateDoctorFailure()];
          }),
        );
      }),
    ),
  );

  deleteDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorDataStoreActions.DeleteDoctor),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.doctorApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [DoctorDataStoreActions.DeleteDoctorSuccessful({ payload })];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [DoctorDataStoreActions.DeleteDoctorFailure()];
          }),
        );
      }),
    ),
  );
}
